import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

/**
 * EditPayoutModal - Admin-only edit for any payout, full control except client_id/cycle_id.
 * Edit reason mandatory; saved to audit log.
 */
export const EditPayoutModal = ({ payout, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!payout) return;
    setForm({
      base_currency: payout.base_currency || 'INR',
      eligible_capital_base: payout.eligible_capital_base ?? '',
      eligible_capital_usdt: payout.eligible_capital_usdt ?? '',
      profit_percent: payout.profit_percent ?? '',
      profit_amount: payout.profit_amount ?? '',
      profit_usdt: payout.profit_usdt ?? '',
      payout_currency: payout.payout_currency || payout.base_currency || 'INR',
      payout_amount: payout.payout_amount ?? '',
      conversion_rate: payout.conversion_rate ?? '',
      conversion_rate_for_usdt: payout.conversion_rate_for_usdt ?? '',
      payment_method: payout.payment_method || '',
      status: payout.status || 'pending',
      reinvested: !!payout.reinvested,
    });
    setReason('');
  }, [payout]);

  if (!payout) return null;

  const numericFields = [
    'eligible_capital_base', 'eligible_capital_usdt', 'profit_percent',
    'profit_amount', 'profit_usdt', 'payout_amount', 'conversion_rate',
    'conversion_rate_for_usdt',
  ];

  const buildPayload = () => {
    const payload = { edit_reason: reason.trim() };
    Object.keys(form).forEach((k) => {
      const original = payout[k];
      const current = form[k];
      if (current === '' || current === null || current === undefined) return;
      if (numericFields.includes(k)) {
        const num = parseFloat(current);
        if (!Number.isNaN(num) && num !== original) payload[k] = num;
      } else if (current !== original) {
        payload[k] = current;
      }
    });
    return payload;
  };

  const handleSave = async () => {
    if (!reason.trim()) {
      toast.error('Edit reason is required');
      return;
    }
    const payload = buildPayload();
    if (Object.keys(payload).length <= 1) {
      toast.error('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await axiosInstance.put(`/payouts/${payout.id}`, payload);
      toast.success('Payout updated');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update payout');
    } finally {
      setSaving(false);
    }
  };

  const num = (label, key, extra = {}) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="number"
        step="0.01"
        value={form[key] ?? ''}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="form-input w-full text-sm"
        data-testid={`edit-payout-${key}`}
        {...extra}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold">Edit Payout: {payout.client_code} — {payout.client_name}</h2>
            <p className="text-xs text-gray-500">All fields editable except client/cycle</p>
          </div>
          <button onClick={onClose} data-testid="edit-payout-close" className="text-gray-500 hover:text-gray-800">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Base Currency</label>
              <select
                value={form.base_currency}
                onChange={(e) => setForm({ ...form, base_currency: e.target.value })}
                className="form-input w-full text-sm"
                data-testid="edit-payout-base_currency"
              >
                <option value="INR">INR</option>
                <option value="USDT">USDT</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payout Currency</label>
              <select
                value={form.payout_currency}
                onChange={(e) => setForm({ ...form, payout_currency: e.target.value })}
                className="form-input w-full text-sm"
                data-testid="edit-payout-payout_currency"
              >
                <option value="INR">INR</option>
                <option value="USDT">USDT</option>
              </select>
            </div>
            {num('Eligible Capital (Base)', 'eligible_capital_base')}
            {num('Eligible Capital (USDT)', 'eligible_capital_usdt')}
            {num('Profit %', 'profit_percent')}
            {num('Profit Amount (Base)', 'profit_amount')}
            {num('Profit (USDT)', 'profit_usdt')}
            {num('Payout Amount', 'payout_amount')}
            {num('Conversion Rate', 'conversion_rate')}
            {num('Conversion Rate (for USDT)', 'conversion_rate_for_usdt')}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Payment Method</label>
              <input
                type="text"
                value={form.payment_method ?? ''}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
                className="form-input w-full text-sm"
                data-testid="edit-payout-payment_method"
                placeholder="e.g. Cash/Bank, USDT Transfer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="form-input w-full text-sm"
                data-testid="edit-payout-status"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="reinvested">Reinvested</option>
              </select>
            </div>
            <label className="flex items-center gap-2 mt-6 text-sm">
              <input
                type="checkbox"
                checked={!!form.reinvested}
                onChange={(e) => setForm({ ...form, reinvested: e.target.checked })}
                data-testid="edit-payout-reinvested"
              />
              Marked as Reinvested
            </label>
          </div>

          <div className="border-t pt-4">
            <label className="block text-xs font-medium text-red-600 mb-1">Edit Reason <span className="text-red-500">*</span></label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="edit-payout-reason"
              rows={2}
              className="form-input w-full text-sm"
              placeholder="Why are you editing this payout? (required for audit log)"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t sticky bottom-0 bg-white">
          <button onClick={onClose} className="btn-secondary text-sm" data-testid="edit-payout-cancel">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary text-sm"
            data-testid="edit-payout-save"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};
